import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getCountryCode } from '../../libs/utils';
import { confirmLocation, getDeliveryInformation } from '../../libs/api';
import './style.scss';

// Regex pattern to identify Google Plus Codes in address strings
const PLUS_CODE_REGEX = /^[23456789CFGHJMPQRVWX]{4,}\+[23456789CFGHJMPQRVWX]{2,}\b/i;

/**
 * ConfirmLocation component allows users to verify and adjust their delivery location
 * using Google Maps integration. Users can search for addresses, drag a marker to
 * the correct position, or use their current location.
 *
 * @param {Object} props - The component's input properties.
 * @param {Object} props.match - React Router match object containing route parameters.
 * @param {Object} props.match.params - Route parameters.
 * @param {string} props.match.params.deliveryId - The unique identifier for the delivery.
 *
 * @returns {JSX.Element} A rendered location confirmation interface with map and controls.
 */
const ConfirmLocation = ({ match }) => {
    // Route parameters and navigation
    const { deliveryId } = match.params;
    const history = useHistory();
    const location = useLocation();

    const skipReverseGeocodeRef = useRef(false)

    // Refs for DOM elements and Google Maps instances
    const mapRef = useRef(null);
    const searchInputRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerInstanceRef = useRef(null);
    const scriptLoadingRef = useRef(false);
    const mapInitializedRef = useRef(false);

    // State management
    const [coordinates, setCoordinates] = useState({ lat: 25.2048, lng: 55.2708 }); // Default Dubai coordinates
    const [selectedLocation, setSelectedLocation] = useState(null);

    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedGooglePlaceId, setSelectedGooglePlaceId] = useState(null);

    const [originalAddress, setOriginalAddress] = useState(null);

    const [isMapLoading, setIsMapLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const selectBestAddress = useCallback((results) => {
        if (!results || !results.length) return null;

        const hasPlusCodeComponent = (r) =>
            Array.isArray(r?.address_components) &&
            r.address_components.some(c => c?.types?.includes('plus_code'));

        // Prefer “real” street address results, and avoid plus-code based ones
        return (
            results.find(r => r?.types?.includes('street_address') && !hasPlusCodeComponent(r)) ||
            results.find(r => r?.types?.includes('premise') && !hasPlusCodeComponent(r)) ||
            results.find(r => r?.types?.includes('route') && !hasPlusCodeComponent(r)) ||
            results.find(r => r?.formatted_address && !PLUS_CODE_REGEX.test(r.formatted_address)) ||
            results[0] ||
            null
        );
    }, []);

    /**
     * Converts latitude and longitude coordinates to a human-readable address
     * using Google Maps Geocoding API. Filters out Plus Code addresses in favor
     * of more readable street addresses.
     *
     * @param {number} lat - The latitude coordinate.
     * @param {number} lng - The longitude coordinate.
     */
    const getAddressFromCoordinates = useCallback((lat, lng) => {
        if (!window.google) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.length) {
                // Prefer addresses without Plus Codes for better readability
                // const validAddress = results.find(result => !PLUS_CODE_REGEX.test(result.formatted_address)) || results[0];
                const validAddress = selectBestAddress(results);
                setSelectedAddress(validAddress?.formatted_address ?? '');
                setSelectedGooglePlaceId(validAddress?.place_id);
            }
        });
    }, [selectBestAddress]);

    /**
     * Clears the search input field and resets the search value state.
     * Also triggers an input event to notify the Google Places SearchBox
     * that the value has been cleared.
     */
    const clearSearchInput = useCallback(() => {
        setSearchValue('');
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
            // Trigger input event to notify SearchBox that the value has changed
            searchInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }, []);

    /**
     * Gets the user's current location using the browser's geolocation API
     * and updates the map center and marker position accordingly.
     * Handles permission denial gracefully by silently failing.
     */
    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            return;
        }

        setIsGettingLocation(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const userLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                setCoordinates({lat: userLocation.lat, lng: userLocation.lng});
                setSelectedLocation(userLocation);

                // Update map center and marker position
                if (mapInstanceRef.current && markerInstanceRef.current) {
                    mapInstanceRef.current.setCenter(userLocation);
                    mapInstanceRef.current.setZoom(16);
                    markerInstanceRef.current.setPosition(userLocation);
                    // setSelectedLocation(userLocation);
                    // getAddressFromCoordinates(userLocation.lat, userLocation.lng);
                }

                getAddressFromCoordinates(userLocation.lat, userLocation.lng);
                setIsGettingLocation(false);
            },
            () => {
                // Silently ignore if permission denied or location unavailable
                setIsGettingLocation(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
            }
        );
    }, [getAddressFromCoordinates]);

    /**
     * Initializes the Google Maps instance with the specified center coordinates.
     * Sets up the map, draggable marker, and search functionality.
     *
     * @param {Object} center - The center coordinates for the map.
     * @param {number} center.lat - The latitude coordinate.
     * @param {number} center.lng - The longitude coordinate.
     */
    const initializeMap = useCallback((center) => {

        const mapInstance = new window.google.maps.Map(mapRef.current, {
            center: center,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
        });

        // Create initial marker
        const markerInstance = new window.google.maps.Marker({
            position: center,
            map: mapInstance,
            draggable: true,
            title: 'Your location'
        });

        mapInstanceRef.current = mapInstance;
        markerInstanceRef.current = markerInstance;

        // Update selected location when marker is dragged
        markerInstance.addListener('dragend', () => {
            const position = markerInstance.getPosition();
            const newLocation = {
                lat: position.lat(),
                lng: position.lng()
            };
            setSelectedLocation(newLocation);
            getAddressFromCoordinates(newLocation.lat, newLocation.lng);
        });

        // Initialize search box
        if (searchInputRef.current) {
            const searchBoxInstance = new window.google.maps.places.SearchBox(searchInputRef.current);
            searchBoxInstance.addListener('places_changed', () => {
                const places = searchBoxInstance.getPlaces();
                if (places.length === 0) return;

                const place = places[0];
                if (place.geometry && place.geometry.location) {
                    const newPosition = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    };

                    // new to get text
                    const selectedText = searchInputRef.current?.value || place.formatted_address || place.name || ''

                    mapInstance.setCenter(newPosition);
                    mapInstance.setZoom(16);

                    skipReverseGeocodeRef.current = true
                    setCoordinates(newPosition)
                    setSelectedLocation(newPosition)
                    setSelectedAddress(selectedText)
                    setSelectedGooglePlaceId(place.place_id || null)
                    setSearchValue(selectedText)
                    /*
                    mapInstance.setCenter(newPosition);
                    mapInstance.setZoom(16);
                    markerInstance.setPosition(newPosition);
                    setSelectedLocation(newPosition);
                    getAddressFromCoordinates(newPosition.lat, newPosition.lng);


                    // Update the search input state to match the selected place
                    setSearchValue(place.formatted_address || place.name);
                    */
                }
            });
        }
        setIsMapLoading(false);
        setSelectedLocation(center);
        mapInitializedRef.current = true;
    }, [getAddressFromCoordinates]);

    const formatAddress = (a = {}) => {
        const clean = (v) => (typeof v === 'string' ? v.trim() : v);

        const parts = [
            clean(a.streetNumber),
            clean(a.street1),
            clean(a.street2),
            clean(a.postalCode),
            clean(a.location), // city/area
        ].filter((v) => typeof v === 'string' && v.length > 0);

        return parts.join(', ');
    };

    /**
     * Fetches delivery information when component mounts and sets initial coordinates.
     * If the address is already verified, redirects to settings page.
     */
    useEffect(() => {
        const fetchDeliveryData = async () => {
            try {
                const deliveryData = await getDeliveryInformation(deliveryId);
                const lat = deliveryData.recipient.address.latitude
                const lng = deliveryData.recipient.address.longitude
                const is_verified = deliveryData.recipient.address.is_verified;

                // If delivery is delivered, reviewed, or cancelled, redirect to home page
                if (["DELIVERED", "REVIEWED", "CANCELLED"].includes(deliveryData.status)) {
                    history.push({
                        pathname: `/${getCountryCode()}/${deliveryId}`,
                        state: {
                            settings: {},
                        },
                    });
                    return;
                } else if(is_verified) {
                    history.push({
                        pathname: `/${getCountryCode()}/settings/${deliveryId}`,
                        state: {
                            settings: {},
                        },
                    });
                    return;
                }

                setOriginalAddress(formatAddress(deliveryData?.recipient?.address));
                setCoordinates({ lat, lng });
                getAddressFromCoordinates(lat, lng);
            } catch (error) {
                console.error('Failed to fetch delivery data:', error);
            }
        };

        if (deliveryId) {
            fetchDeliveryData();
        }
    }, [deliveryId, getAddressFromCoordinates, history]);

    /**
     * Updates map center and marker position when coordinates change.
     * Only runs when the map has been initialized.
     */
    useEffect(() => {
        if (mapInstanceRef.current?.setCenter && coordinates && mapInitializedRef.current) {
            mapInstanceRef.current.setCenter(coordinates);
            mapInstanceRef.current.setZoom(16); // Zoom to a closer level when searching
            markerInstanceRef.current.setPosition(coordinates);
            setSelectedLocation(coordinates);

            if (skipReverseGeocodeRef.current) {
                skipReverseGeocodeRef.current = false
                return
            }

            getAddressFromCoordinates(coordinates.lat, coordinates.lng);
        }
    }, [coordinates, getAddressFromCoordinates]);

    /**
     * Global callback function for Google Maps script loading.
     * Called by Google Maps API when the script finishes loading.
     */
    window.initializeMapWithGoogle = () => {
        // Check if the google.maps object is available after script loading
        if (window.google && window.google.maps) {
            // Use coordinates state for map initialization
            initializeMap(coordinates);
        } else {
            setError('Google Maps is not available.');
            setIsMapLoading(false);
        }
    };

    /**
     * Initializes Google Maps after component renders.
     * Loads the Google Maps script if not already loaded and sets up error handling.
     */
    useEffect(() => {
        // Prevent multiple initializations
        if (mapInitializedRef.current) {
            return;
        }

        const initMap = () => {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

            if (!apiKey) {
                setError('Google Maps API key is not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.');
                return;
            }

            // Load Google Maps script if not already loaded
            if (!window.google && !scriptLoadingRef.current) {
                scriptLoadingRef.current = true;
                setIsMapLoading(true);
                const script = document.createElement('script');
                script.async = true;
                script.defer = true;
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&callback=initializeMapWithGoogle`;
                script.onerror = () => {
                    setError('Failed to load Google Maps');
                    setIsMapLoading(false);
                    scriptLoadingRef.current = false;
                };
                document.head.appendChild(script);
            } else if (window.google) {
                // Google Maps already loaded, initialize map directly
                setIsMapLoading(true);
                window.initializeMapWithGoogle();
            }
        };

        initMap();
    }, []); // Empty dependency array - run only once


    /**
     * Handles the location confirmation process.
     * Sends the selected coordinates to the API and navigates to the main delivery page.
     */
    const handleConfirmLocation = async () => {
        if (selectedLocation && !isSubmitting) {
            setIsSubmitting(true);
            try {
                await confirmLocation(deliveryId,
                    {lat: selectedLocation.lat, long: selectedLocation.lng, selectedAddress: selectedAddress, selected_place_id: selectedGooglePlaceId},
                );
                const countryCode = getCountryCode();
                history.push({
                    pathname: `/${countryCode}/${deliveryId}`,
                    state: {
                        ...location.state,
                        confirmedLocation: selectedLocation
                    }
                });
            } catch (error) {
                console.error('Failed to confirm location:', error);
                setError('Failed to confirm location. Please try again.');
                setIsSubmitting(false);
            }
        }
    };


    // Render error state if there's an error
    if (error) {
        return (
            <div className="confirm-location">
                <div className="error">
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    // Main component render
    return (
        <div className="confirm-location">

            {/* Information section with instructions */}
            <div className="confirm-location__info">
                <div className="info-content">
                    <p>Please confirm your location on the map. If it's not correct, you can search for your address, move the pin to the exact location, or tap the location icon next to the Confirm location button.</p>
                </div>
            </div>

            {/* Map container with search functionality */}
            <div className="confirm-location__map">
                <div className="confirm-location__search">
                    <div className="search-input-wrapper">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search for an address..."
                            className="search-input"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                        {searchValue ? (
                            <button
                                className="clear-button"
                                onClick={clearSearchInput}
                                type="button"
                                title="Clear search"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        ) : (
                            <div className="search-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        )}
                    </div>
                </div>
                <div ref={mapRef} className="map-container"></div>
                {isMapLoading && (
                    <div className="map-loading-overlay">
                        <div className="spinner"></div>
                        <p>Loading map...</p>
                    </div>
                )}
            </div>

            {/* Display selected address */}
            {(selectedAddress || originalAddress) && (
                <div className="confirm-location__address">
                    <div className="address-content">
                        <div className="address-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" fill="#666"/>
                            </svg>
                        </div>
                        <span className="address-text">{selectedAddress || originalAddress}</span>
                    </div>
                </div>
            )}

            {/* Action buttons */}
            <div className="confirm-location__actions">
                <div className="actions-container">
                    <button
                        className="confirm-button"
                        onClick={handleConfirmLocation}
                        disabled={!selectedLocation || isSubmitting}
                    >
                        {isSubmitting ? 'Confirming...' : 'Confirm location'}
                    </button>
                </div>
            </div>

            {/* Current location button */}
            <button
                className="my-location-button"
                onClick={getCurrentLocation}
                disabled={isGettingLocation}
                title="Use my current location"
            >
                {isGettingLocation ? (
                    <div className="location-spinner"></div>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'scale(1.1)' }}>
                        <path d="M12,2 C12.3796958,2 12.693491,2.28215388 12.7431534,2.64822944 L12.75,2.75 L12.7490685,4.53770881 L12.7490685,4.53770881 C16.292814,4.88757432 19.1124257,7.70718602 19.4632195,11.2525316 L19.5,11.25 L21.25,11.25 C21.6642136,11.25 22,11.5857864 22,12 C22,12.3796958 21.7178461,12.693491 21.3517706,12.7431534 L21.25,12.75 L19.4616558,12.7490368 L19.4616558,12.7490368 C19.1124257,16.292814 16.292814,19.1124257 12.7474684,19.4632195 L12.75,19.5 L12.75,21.25 C12.75,21.6642136 12.4142136,22 12,22 C11.6203042,22 11.306509,21.7178461 11.2568466,21.3517706 L11.25,21.25 L11.2509632,19.4616558 L11.2509632,19.4616558 C7.70718602,19.1124257 4.88757432,16.292814 4.53678051,12.7474684 L4.5,12.75 L2.75,12.75 C2.33578644,12.75 2,12.4142136 2,12 C2,11.6203042 2.28215388,11.306509 2.64822944,11.2568466 L2.75,11.25 L4.53770881,11.2509315 L4.53770881,11.2509315 C4.88757432,7.70718602 7.70718602,4.88757432 11.2525316,4.53678051 L11.25,4.5 L11.25,2.75 C11.25,2.33578644 11.5857864,2 12,2 Z M12,6 C8.6862915,6 6,8.6862915 6,12 C6,15.3137085 8.6862915,18 12,18 C15.3137085,18 18,15.3137085 18,12 C18,8.6862915 15.3137085,6 12,6 Z M12,8 C14.209139,8 16,9.790861 16,12 C16,14.209139 14.209139,16 12,16 C9.790861,16 8,14.209139 8,12 C8,9.790861 9.790861,8 12,8 Z"/>
                    </svg>
                )}
            </button>
        </div>
    );
};

export default ConfirmLocation;
